import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Node, Edge } from 'org-graph/types';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getCompanyRole } from '../i18n/backendTranslations';
import { formatDate } from '../i18n/formatters';

interface RelationshipsTableProps {
  nodes: Node[];
  relationships: Edge[];
  sourceNodeId: string;
}

function calculateBinnedDuration(startDate: string | undefined, endDate: string | undefined, t: TFunction): string {
    if (!startDate) return t('common.states.unknown');
  
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffInMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  
    if (diffInMonths < 6) return t('graph.durationBins.underSixMonths');
    if (diffInMonths < 12) return t('graph.durationBins.sevenToTwelveMonths');
    if (diffInMonths < 120) {
      const from = Math.floor(diffInMonths / 12);
      return t('graph.durationBins.years', { from, to: from + 1 });
    }
    return t('graph.durationBins.tenPlusYears');
  }

const RelationshipsTable: React.FC<RelationshipsTableProps> = ({ nodes, relationships, sourceNodeId }) => {
  const { t, i18n } = useTranslation();
  // Find the source node
  const sourceNode = nodes.find(node => node.id === sourceNodeId);

  if (!sourceNode) {
    return <p>{t('assessment.details.sourceNodeNotFound')}</p>;
  }

  // Filter relationships connected to the source node
  const connectedRelationships = relationships.filter(
    relationship => relationship.from === sourceNodeId || relationship.to === sourceNodeId
  );

  // Sort relationships with missing end dates first, then by actual end dates.
  const sortedRelationships = connectedRelationships.sort((a, b) => {
    const aEndDate = a.properties.end_date;
    const bEndDate = b.properties.end_date;

    if (!aEndDate && bEndDate) return -1;
    if (aEndDate && !bEndDate) return 1;
    if (!aEndDate || !bEndDate) return 0;

    return new Date(aEndDate).getTime() - new Date(bEndDate).getTime();
  });

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'action.hover' }}>
            <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.details.role')}</TableCell>
            <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('common.fields.startDate')}</TableCell>
            <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('common.fields.endDate')}</TableCell>
            <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.details.duration')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRelationships.map((relationship, index) => (
            <TableRow key={index}>
              <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{getCompanyRole(relationship.properties.funktionar_funk_text || '')}</TableCell>
              <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{formatDate(relationship.properties.start_date, i18n.resolvedLanguage) || t('common.states.notAvailable')}</TableCell>
              <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{formatDate(relationship.properties.end_date, i18n.resolvedLanguage) || t('common.states.notAvailable')}</TableCell>
              <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{calculateBinnedDuration(relationship.properties.start_date, relationship.properties.end_date, t)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RelationshipsTable;
